import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type Citation, type Message, type Project, type Thread } from "../api/client";
import { postSseStream, type PhaseName } from "../api/sse";
import AttributionBadge from "../components/Attribution";
import ChatStream from "../components/ChatStream";
import Icon from "../components/Icon";
import { useDraft } from "../hooks/useDraft";
import { getCurrentProject } from "../hooks/useCurrentProject";
import {
  FIELD_NOTE,
  STUDY_DELIVERABLES,
  profileForStudy,
  studyInitials,
} from "../data/studyFixtures";

interface Phase {
  name: PhaseName;
  count?: number;
}

function phaseLabel(p: Phase): string {
  switch (p.name) {
    case "retrieving":
      return "Searching the library…";
    case "retrieved":
      return p.count
        ? `Reading ${p.count} source${p.count === 1 ? "" : "s"}…`
        : "Reading…";
    case "generating":
      return "Drafting response…";
  }
}

export default function ChatPage() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const numericThreadId = threadId ? Number(threadId) : null;

  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useDraft<string>(`abby_draft:chat:${numericThreadId ?? "new"}`, "");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [streamCitations, setStreamCitations] = useState<Citation[]>([]);
  const [study, setStudy] = useState<Project | null>(null);
  const [activeThread, setActiveThread] = useState<Thread | null>(null);
  const [relatedThreads, setRelatedThreads] = useState<Thread[]>([]);
  const [phase, setPhase] = useState<Phase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesEnd = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setError(null);
    setStreamText("");
    setStreamCitations([]);
    if (numericThreadId == null) {
      setMessages([]);
      return;
    }
    api
      .getMessages(numericThreadId)
      .then(setMessages)
      .catch((e) => setError(String(e)));
  }, [numericThreadId]);

  useEffect(() => {
    const cp = getCurrentProject();
    Promise.all([api.listProjects(true), api.listThreads()])
      .then(([projects, threads]) => {
        const activeThread = numericThreadId == null ? null : threads.find((t) => t.id === numericThreadId) ?? null;
        const studyId = activeThread?.project_id ?? (cp.kind === "project" ? cp.id : null);
        const nextStudy = studyId == null ? null : projects.find((p) => p.id === studyId) ?? null;
        setActiveThread(activeThread);
        setStudy(nextStudy);
        setRelatedThreads(studyId == null ? [] : threads.filter((t) => t.project_id === studyId && t.id !== numericThreadId));
      })
      .catch(() => {});
  }, [numericThreadId]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, streamText]);

  async function send() {
    if (!draft.trim() || streaming) return;
    const text = draft.trim();
    setDraft("");
    setStreaming(true);
    setStreamText("");
    setStreamCitations([]);
    setPhase(null);
    setError(null);

    const optimistic: Message = {
      id: -Date.now(),
      thread_id: numericThreadId ?? 0,
      role: "user",
      content: text,
      citations: [],
      token_usage: null,
      created_at: Date.now(),
    };
    setMessages((m) => [...m, optimistic]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let newThreadId: number | null = numericThreadId;

    try {
      // If a project is currently selected in the sidebar, pass it so the
      // server-side new-thread creation puts the thread in that project.
      const cp = getCurrentProject();
      const project_id =
        numericThreadId == null && cp.kind === "project" ? cp.id : null;

      for await (const evt of postSseStream(
        "/api/chat/stream",
        { thread_id: numericThreadId, content: text, project_id },
        ctrl.signal,
      )) {
        if (evt.type === "thread") {
          newThreadId = evt.thread_id;
          if (numericThreadId == null) {
            // Update URL without remounting and losing stream state
            window.history.replaceState({}, "", `/chat/${evt.thread_id}`);
          }
        } else if (evt.type === "phase") {
          setPhase({ name: evt.name, count: evt.count });
        } else if (evt.type === "retrieved") {
          setStreamCitations(evt.citations);
        } else if (evt.type === "token") {
          setPhase(null);
          setStreamText((s) => s + evt.text);
        } else if (evt.type === "thinking") {
          // (Optional UI: show a thinking indicator. Skipped for now to keep the message panel clean.)
        } else if (evt.type === "error") {
          setError(evt.detail);
        } else if (evt.type === "done") {
          break;
        }
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStreaming(false);
      abortRef.current = null;
      if (newThreadId != null) {
        // Re-pull persisted messages so we replace the optimistic ones with the canonical rows
        api.getMessages(newThreadId).then(setMessages).catch(() => {});
        if (numericThreadId == null) navigate(`/chat/${newThreadId}`, { replace: true });
      }
      setStreamText("");
      setStreamCitations([]);
      setPhase(null);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function useStarterPrompt(prompt: string) {
    setDraft(prompt);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  const railCitations = useMemo(() => {
    const source = streamCitations.length > 0 ? streamCitations : messages.flatMap((m) => m.citations);
    const seen = new Set<number>();
    return source.filter((c) => {
      if (!c.source_id || seen.has(c.source_id)) return false;
      seen.add(c.source_id);
      return true;
    });
  }, [messages, streamCitations]);

  const chatBody = (
    <>
      <div className="chat-messages">
        {messages.length === 0 && !streaming && !error && (
          <ChatEmptyState study={study} onPickPrompt={useStarterPrompt} />
        )}

        {messages.map((m) => (
          <div key={m.id} className={`msg ${m.role}`}>
            <span className="role">{m.role}</span>
            <div className="body">
              {m.role === "assistant" ? (
                <ChatStream content={m.content} citations={m.citations} />
              ) : (
                <div style={{ whiteSpace: "pre-wrap" }}>{m.content}</div>
              )}
            </div>
          </div>
        ))}

        {streaming && (
          <div className="msg assistant">
            <span className="role">assistant</span>
            {streamCitations.length > 0 && (
              <div className="retrieved-banner">
                <span className="label">Retrieved:</span>
                {streamCitations.slice(0, 8).map((c, i) => (
                  <span key={`${c.source_id}-${i}`} className="tag">
                    {c.title}
                    {c.year ? ` (${c.year})` : ""}
                  </span>
                ))}
              </div>
            )}
            <div className="body">
              {streamText ? (
                <ChatStream content={streamText} citations={streamCitations} />
              ) : (
                <span className="muted phase-indicator">
                  <span className="spinner" />
                  {phase ? phaseLabel(phase) : "Working…"}
                </span>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="msg assistant">
            <span className="role error">error</span>
            <div className="body error">{error}</div>
          </div>
        )}
        <div ref={messagesEnd} />
      </div>
      <div className="chat-input">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about IRB, sampling, instruments, methodology, FSL frameworks…  (Enter to send, Shift+Enter for newline)"
        />
        <button className="primary" onClick={send} disabled={streaming || !draft.trim()}>
          Send
        </button>
      </div>
    </>
  );

  return (
    <>
      <div className="main-header chat-header">
        <div>
          {study && (
            <span className="crumb">
              <span className="project-dot" style={{ background: study.color || "var(--accent)" }} />
              studies / {study.name} / thread
            </span>
          )}
          <h2>{numericThreadId ? "Conversation" : "New conversation"}</h2>
          <AttributionBadge createdBy={activeThread?.created_by} compact />
        </div>
        <span className="spacer" />
        {study && <button className="ghost-link">Pin to a deliverable</button>}
        {numericThreadId != null && !streaming && messages.length > 0 && (
          <a
            className="ghost-link"
            href={`/api/threads/${numericThreadId}/export.md`}
            title="Download this conversation as Markdown"
          >
            Export
          </a>
        )}
        {streaming && (
          <button
            className="ghost"
            onClick={() => abortRef.current?.abort()}
            title="Stop streaming"
          >
            Stop
          </button>
        )}
      </div>
      {study ? (
        <div className="study-chat-layout">
          <div className="chat study-chat-pane">{chatBody}</div>
          <StudyChatRail study={study} citations={railCitations} threads={relatedThreads} />
        </div>
      ) : (
        <div className="chat">{chatBody}</div>
      )}
    </>
  );
}

function ChatEmptyState({
  study,
  onPickPrompt,
}: {
  study: Project | null;
  onPickPrompt: (prompt: string) => void;
}) {
  const profile = study ? profileForStudy(study) : null;
  const prompts = study
    ? [
        `What changed most recently in ${study.name}?`,
        `Turn the current notes into the next three fieldwork questions.`,
        `Find source gaps for this study question.`,
      ]
    : [
        "Compare the current evidence across my active studies.",
        "Draft a methods memo from recent notes.",
        "Find gaps in the library around food security livelihoods methods.",
      ];

  return (
    <section className="chat-empty-state">
      <span className="empty-state-icon" aria-hidden="true">
        <Icon name="chat" size={18} />
      </span>
      <div>
        <span className="crumb">{study ? "scoped conversation" : "new conversation"}</span>
        <h3>{study ? `Start a chat for ${study.name}` : "Start a new conversation"}</h3>
        <p>
          {profile
            ? profile.question
            : "Use the workspace context, library sources, and prior research activity as grounding."}
        </p>
      </div>
      <div className="empty-prompt-grid">
        {prompts.map((prompt) => (
          <button key={prompt} type="button" onClick={() => onPickPrompt(prompt)}>
            {prompt}
          </button>
        ))}
      </div>
    </section>
  );
}

function StudyChatRail({
  study,
  citations,
  threads,
}: {
  study: Project;
  citations: Citation[];
  threads: Thread[];
}) {
  const navigate = useNavigate();
  const profile = profileForStudy(study);
  const sourceRows =
    citations.length > 0
      ? citations.slice(0, 4).map((c, index) => ({
          n: index + 1,
          title: c.title || "Untitled source",
          sub: [c.authors, c.year].filter(Boolean).join(" - "),
        }))
      : [];

  return (
    <aside className="study-chat-rail">
      <section className="rail-group">
        <div className="head">study context</div>
        <div className="study-rail-card">
          <span className="icobox" style={{ background: study.color || "var(--accent)" }}>
            {studyInitials(study.name)}
          </span>
          <div>
            <strong>{study.name}</strong>
            <span>{profile.phase}</span>
          </div>
        </div>
      </section>

      {STUDY_DELIVERABLES.length > 0 && (
        <section className="rail-group">
          <div className="head">this thread touches <span>{STUDY_DELIVERABLES.slice(0, 2).length} deliverables</span></div>
          {STUDY_DELIVERABLES.slice(1, 3).map((d) => (
            <button key={d.key} type="button" className="rail-item" onClick={() => navigate(`/studies/${study.id}/deliverables/${d.key}`)}>
              <span className="icobox">{studyInitials(d.kind)}</span>
              <span>
                <small>deliverable</small>
                <strong>{d.kind}</strong>
                <em>{d.status}</em>
              </span>
            </button>
          ))}
        </section>
      )}

      <section className="rail-group">
        <div className="head">retrieved sources <span>{sourceRows.length} used</span></div>
        {sourceRows.length === 0 ? (
          <p className="muted">No retrieved sources yet.</p>
        ) : (
          sourceRows.map((source) => (
            <button key={`${source.n}-${source.title}`} type="button" className="rail-item" onClick={() => navigate("/library")}>
              <span className="citebox">{source.n}</span>
              <span>
                <strong>{source.title}</strong>
                <em>{source.sub || "source in library"}</em>
              </span>
            </button>
          ))
        )}
      </section>

      {FIELD_NOTE && (
        <section className="rail-group">
          <div className="head">pinned - field note</div>
          <div className="pinned-note">
            <span>{FIELD_NOTE.title}</span>
            {FIELD_NOTE.body}
          </div>
        </section>
      )}

      <section className="rail-group">
        <div className="head">related threads <span>{threads.length}</span></div>
        {threads.slice(0, 3).map((t) => (
          <button key={t.id} type="button" className="rail-item" onClick={() => navigate(`/chat/${t.id}`)}>
            <span>
              <small>chat</small>
              <strong>{t.title}</strong>
              <AttributionBadge createdBy={t.created_by} compact />
              <em>{new Date(t.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</em>
            </span>
          </button>
        ))}
        {threads.length === 0 && <p className="muted">No related threads yet.</p>}
      </section>
    </aside>
  );
}
